import { AccountingDraftEditRequest } from './shared/accounting-draft-edit.model';
/* eslint-disable */
import { Injectable, Injector, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment'
import {APIListWrapper} from "../models/common/api-list-wrapper.model";
import {AccountReceivables} from "./receivables/receivables.model";
import {catchError, firstValueFrom, map, Observable, throwError} from "rxjs";
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { LocaleService } from '../i18n/locale.service';
import { PendingActionTracker } from '../shared/state/pending-action-tracker';
import {AccountPayables} from "./payables/payables.model";
import {Bill, NonPoBillDraftUpdateRequest} from "./payables/generated-bills/bill.model";
import {Voucher} from "./payables/generated-vouchers/voucher.model";
import {Invoice} from "./invoice/invoice.model";
import {Receipt} from "./receipt/receipt.model";
import {CreditNote} from "./credit-note/credit-note.model";
import {DebitNote} from "./debit-note/debit-note.model";
import {GeneratedReceiptsSummaryData} from "./receivables/generated-receipts/generated-receipts-summary-data.model";
import { GeneratedInvoicesSummaryData } from './receivables/generated-invoices/generated-invoices-summary-data.model';
import { InvoiceDispatch, InvoiceDispatchCandidate, InvoiceDispatchCandidateGroup, InvoiceDispatchEmployee, InvoiceDispatchOverlap, InvoiceDispatchSummary } from './receivables/invoice-dispatch/invoice-dispatch.model';
import { GeneratedVouchersSummaryData } from './payables/generated-vouchers/generated-voucher-summary-data.model';
import { ReceiptAllocationRequest, VoucherAllocationRequest } from './allocation.model';
import {
    AccountingDocumentAction,
    AccountingDocumentType,
    DocumentAuditEvent,
    DocumentWorkflowActionRequest,
    GlPreview
} from './accounting-lifecycle.model';
import {
    AccountingPeriodLock,
    ManualEntryPolicy,
    ManualEntryReasonCode,
    ManualEntryValidationRequest,
    ManualEntryValidationResult
} from './manual-entry-control.model';
import { AccountingDocumentAttachment } from './accounting-attachment.model';
import { AccountingTaxCode } from './accounting-tax-code.model';
import { AccountingComplianceEvent, EInvoiceComplianceDashboard, ZatcaBusinessRegistration, ZatcaOnboardingStatus, ZatcaOnboardingResponse, BayanOnboardingStatus, BayanOnboardingResponse } from './accounting-compliance.model';
import { AccountsOverviewDashboard } from './accounts-dashboard.model';
import { PaymentTerm } from '../order/models/payment-term.model';
import {
    ApMatchingDashboard,
    ApMatchingPolicy,
    ApMatchingTolerance,
    BillMatchEvaluation,
    BillMatchEvaluationRequest,
    PaymentProposal,
    PaymentProposalGenerateRequest,
    RemittanceAdvice,
    VendorBankAccountAudit
} from './payables/phase7-ap-controls.model';
import { BankAccount } from './bank-account/bank-account.model';
import { downloadFinancePdf, FinanceDocumentKind, financePdfVariant, rememberFinancePdfVariant } from './finance-document-download';

export interface FinancePdfStatus { status: 'DRAFT' | 'ORIGINAL' | 'LEGACY' | 'MISSING'; reprintAvailable: boolean; }

export interface CommercialAnnotationsUpdateRequest {
    rowVersion: number;
    externalReferenceNumber?: string | null;
    dueDate?: string;
    logisticsProjectId?: number | null;
    crmClientContactId?: number | null;
    lineComments: Array<{ lineId: number; comment?: string | null }>;
}

export interface AccountingDocumentGroup {
    groupKey: string;
    groupLabel: string;
    currencyCode: string;
    pendingCount: number;
    pendingAmount: number;
    completedCount: number;
    completedAmount: number;
}

@Injectable({
    providedIn: 'root'
})
export class AccountsService {

    private apiUrl = `${environment.restApiUrl}accounts-endpoint`; // Adjust if your API base URL is different
    private readonly injector = inject(Injector);

    constructor(private http: HttpClient) { }

    getInvoiceDispatches(companyId: number | undefined, companyGroupId: number | undefined,
        page: number, pageSize: number, status?: string, searchText?: string): Observable<APIListWrapper<InvoiceDispatchSummary>> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        if (companyId) params = params.set('companyId', companyId);
        if (companyGroupId) params = params.set('companyGroupId', companyGroupId);
        if (status) params = params.set('status', status);
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<InvoiceDispatchSummary>>(`${this.apiUrl}/invoice-dispatches`, { params });
    }

    getInvoiceDispatch(id: number): Observable<InvoiceDispatch> {
        return this.http.get<InvoiceDispatch>(`${this.apiUrl}/invoice-dispatches/${id}`);
    }

    getInvoiceDispatchCandidateGroups(companyId: number, page: number, pageSize: number,
        searchText = ''): Observable<APIListWrapper<InvoiceDispatchCandidateGroup>> {
        let params = new HttpParams().set('companyId', companyId).set('page', page).set('pageSize', pageSize);
        if (searchText.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<InvoiceDispatchCandidateGroup>>(`${this.apiUrl}/invoice-dispatches/candidate-groups`, { params });
    }

    getInvoiceDispatchCandidates(companyId: number, recipientType: string, recipientId: number,
        page: number, pageSize: number, searchText = ''): Observable<APIListWrapper<InvoiceDispatchCandidate>> {
        let params = new HttpParams().set('companyId', companyId).set('recipientType', recipientType)
            .set('recipientId', recipientId).set('page', page).set('pageSize', pageSize);
        if (searchText.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<InvoiceDispatchCandidate>>(`${this.apiUrl}/invoice-dispatches/candidates`, { params });
    }

    getInvoiceDispatchOverlaps(companyId: number, invoiceIds: number[]): Observable<InvoiceDispatchOverlap[]> {
        const params = new HttpParams().set('companyId', companyId);
        return this.http.post<InvoiceDispatchOverlap[]>(`${this.apiUrl}/invoice-dispatches/overlaps`, invoiceIds, { params });
    }

    getInvoiceDispatchEmployees(companyId: number, searchText = ''): Observable<InvoiceDispatchEmployee[]> {
        let params = new HttpParams().set('companyId', companyId);
        if (searchText.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<InvoiceDispatchEmployee[]>(`${this.apiUrl}/invoice-dispatches/employees`, { params });
    }

    createInvoiceDispatch(request: { companyId: number; invoiceIds: number[];
        representativeEmployeeId: number; repeatReason?: string }): Observable<InvoiceDispatch> {
        return this.http.post<InvoiceDispatch>(`${this.apiUrl}/invoice-dispatches`, request);
    }

    downloadInvoiceDispatch(id: number): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/invoice-dispatches/${id}/original`, { responseType: 'blob' });
    }

    recordInvoiceDispatchProof(id: number, files: { url: string; filename: string }[], receivedDate: string,
        recipientName: string, recipientTitle: string): Observable<InvoiceDispatch> {
        return this.http.post<InvoiceDispatch>(`${this.apiUrl}/invoice-dispatches/${id}/proofs`, {
            files, receivedDate, signedStampedConfirmed: true,
            recipientName: recipientName.trim() || null, recipientTitle: recipientTitle.trim() || null
        });
    }

    downloadInvoiceDispatchProof(id: number, proofId: number): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/invoice-dispatches/${id}/proofs/${proofId}`, { responseType: 'blob' });
    }

    voidInvoiceDispatch(id: number, reason: string): Observable<InvoiceDispatch> {
        return this.http.post<InvoiceDispatch>(`${this.apiUrl}/invoice-dispatches/${id}/void`, { reason });
    }

    getDocumentGroups(documentType: string, groupBy: 'CRM' | 'PARTY_TYPE', page: number,
        pageSize: number, filters: Record<string, string | number | boolean | number[] | string[] | null | undefined>)
        : Observable<APIListWrapper<AccountingDocumentGroup>> {
        let params = new HttpParams().set('groupBy', groupBy).set('page', page).set('pageSize', pageSize);
        for (const [key, value] of Object.entries(filters)) {
            if (value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length)) {
                params = params.set(key, Array.isArray(value) ? value.join(',') : String(value));
            }
        }
        return this.http.get<APIListWrapper<AccountingDocumentGroup>>(
            `${this.apiUrl}/document-groups/${documentType}`, { params });
    }

    getOverviewDashboard(filters: {
        owningCompanyId?: number;
        companyGroupId?: number;
        memberCompanyId?: number;
        startDate?: string;
        endDate?: string;
        currencyCode?: string;
    }): Observable<AccountsOverviewDashboard> {
        let params = new HttpParams();
        if (filters.owningCompanyId) params = params.set('owningCompanyId', filters.owningCompanyId.toString());
        if (filters.companyGroupId) params = params.set('companyGroupId', filters.companyGroupId.toString());
        if (filters.memberCompanyId) params = params.set('memberCompanyId', filters.memberCompanyId.toString());
        if (filters.startDate) params = params.set('startDate', filters.startDate);
        if (filters.endDate) params = params.set('endDate', filters.endDate);
        if (filters.currencyCode) params = params.set('currencyCode', filters.currencyCode);
        return this.http.get<AccountsOverviewDashboard>(`${this.apiUrl}/dashboard/overview`, { params });
    }

    private applyCompanyScope(params: HttpParams, companyParamName: 'companyId' | 'owningCompanyId', companyId?: number, companyGroupId?: number): HttpParams {
        if (companyGroupId) {
            return params.set('companyGroupId', companyGroupId.toString());
        }
        if (companyId) {
            return params.set(companyParamName, companyId.toString());
        }
        return params;
    }

    getManualEntryReasonCodes(owningCompanyId: number, documentType: string): Observable<ManualEntryReasonCode[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('documentType', documentType);
        return this.http.get<ManualEntryReasonCode[]>(`${this.apiUrl}/manual-entry/reason-codes`, { params });
    }

    saveManualEntryReasonCode(reasonCode: ManualEntryReasonCode): Observable<ManualEntryReasonCode> {
        return this.http.post<ManualEntryReasonCode>(`${this.apiUrl}/manual-entry/reason-codes`, reasonCode);
    }

    getManualEntryPolicies(owningCompanyId: number): Observable<ManualEntryPolicy[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ManualEntryPolicy[]>(`${this.apiUrl}/manual-entry/policies`, { params });
    }

    saveManualEntryPolicy(policy: ManualEntryPolicy): Observable<ManualEntryPolicy> {
        return this.http.post<ManualEntryPolicy>(`${this.apiUrl}/manual-entry/policies`, policy);
    }

    validateManualEntry(request: ManualEntryValidationRequest): Observable<ManualEntryValidationResult> {
        return this.http.post<ManualEntryValidationResult>(`${this.apiUrl}/manual-entry/validate`, request);
    }

    getAccountingTaxCodes(owningCompanyId?: number): Observable<AccountingTaxCode[]> {
        let params = new HttpParams();
        if (owningCompanyId) {
            params = params.set('owningCompanyId', owningCompanyId.toString());
        }
        return this.http.get<AccountingTaxCode[]>(`${this.apiUrl}/tax-codes`, { params });
    }

    saveAccountingTaxCode(taxCode: AccountingTaxCode): Observable<AccountingTaxCode> {
        return this.http.post<AccountingTaxCode>(`${this.apiUrl}/tax-codes`, taxCode);
    }

    getPaymentTerms(owningCompanyId?: number): Observable<PaymentTerm[]> {
        const params = owningCompanyId ? new HttpParams().set('owningCompanyId', owningCompanyId.toString()) : undefined;
        return this.http.get<PaymentTerm[]>(`${environment.restApiUrl}orders/payment-terms`, { params });
    }

    savePaymentTerm(paymentTerm: PaymentTerm, owningCompanyId?: number): Observable<PaymentTerm> {
        const params = owningCompanyId ? new HttpParams().set('owningCompanyId', owningCompanyId.toString()) : undefined;
        if (paymentTerm.paymentTermID) {
            return this.http.put<PaymentTerm>(`${environment.restApiUrl}orders/payment-terms/${paymentTerm.paymentTermID}`, paymentTerm, { params });
        }
        return this.http.post<PaymentTerm>(`${environment.restApiUrl}orders/payment-terms`, paymentTerm, { params });
    }

    archivePaymentTerm(paymentTermId: number, owningCompanyId?: number): Observable<void> {
        const params = owningCompanyId ? new HttpParams().set('owningCompanyId', owningCompanyId.toString()) : undefined;
        return this.http.delete<void>(`${environment.restApiUrl}orders/payment-terms/${paymentTermId}`, { params });
    }

    getAccountingPeriodLocks(owningCompanyId: number): Observable<AccountingPeriodLock[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<AccountingPeriodLock[]>(`${this.apiUrl}/period-locks`, { params });
    }

    saveAccountingPeriodLock(periodLock: AccountingPeriodLock): Observable<AccountingPeriodLock> {
        return this.http.post<AccountingPeriodLock>(`${this.apiUrl}/period-locks`, periodLock);
    }

    unlockAccountingPeriodLock(periodLockId: number, reason: string): Observable<AccountingPeriodLock> {
        return this.http.patch<AccountingPeriodLock>(`${this.apiUrl}/period-locks/${periodLockId}/unlock`, { reason });
    }

    getGeneratedInvoices(
        page: number,
        pageSize: number,
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        invoiceNumber?: string,
        extraFilters?: {
            documentStatuses?: string[];
            workflowStatuses?: string[];
            postingStatuses?: string[];
            paymentStatuses?: string[];
            dueStatus?: string;
            sentStatuses?: string[];
            collectionStatuses?: string[];
            zatcaStatuses?: string[];
            currencyCode?: string;
            amountMin?: number;
            amountMax?: number;
            partyId?: number;
            partyType?: string;
        },
        companyGroupId?: number,
        searchText?: string
    ): Observable<APIListWrapper<Invoice>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString())

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        params = this.applyCompanyScope(params, 'companyId', companyId, companyGroupId);
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (invoiceNumber) {
            params = params.set('invoiceNumber', invoiceNumber);
        }
        params = this.appendInvoiceFilters(params, extraFilters);
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<Invoice>>(`${this.apiUrl}/invoices/generated`, { params });
    }

    getInvoiceById(invoiceId: number): Observable<Invoice> {
        return this.http.get<Invoice>(`${this.apiUrl}/invoices/${invoiceId}`);
    }

    downloadInvoiceDocument(invoiceId: number): Observable<Blob> {
        return this.downloadStoredDocument(`invoices/${invoiceId}/document`);
    }

    invoiceDocumentStatus(invoiceId: number): Observable<FinancePdfStatus> {
        return this.http.get<FinancePdfStatus>(`${this.apiUrl}/invoices/${invoiceId}/document/status`);
    }

    reprintInvoiceDocument(invoiceId: number): Observable<Blob> {
        return this.downloadStoredDocument(`invoices/${invoiceId}/document/reprint`);
    }

    createManualInvoiceDraft(invoice: Invoice): Observable<Invoice> {
        return this.http.post<Invoice>(`${this.apiUrl}/invoices/manual-draft`, invoice);
    }

    editInvoice(invoiceId: number, request: AccountingDraftEditRequest): Observable<Invoice> {
        return this.http.patch<Invoice>(`${this.apiUrl}/invoices/${invoiceId}`, request);
    }
    editAccountReceivable(id: number, request: AccountingDraftEditRequest): Observable<AccountReceivables> {
        return this.http.patch<AccountReceivables>(`${this.apiUrl}/account-receivables/${id}`, request);
    }
    editAccountPayable(id: number, request: AccountingDraftEditRequest): Observable<AccountPayables> {
        return this.http.patch<AccountPayables>(`${this.apiUrl}/account-payables/${id}`, request);
    }

    updateInvoice(invoiceId: number, invoice: Invoice): Observable<Invoice> {
        return this.http.put<Invoice>(`${this.apiUrl}/invoices/${invoiceId}`, invoice);
    }

    updateInvoiceCommercialAnnotations(invoiceId: number, request: CommercialAnnotationsUpdateRequest): Observable<Invoice> {
        return this.http.patch<Invoice>(`${this.apiUrl}/invoices/${invoiceId}/commercial-annotations`, request);
    }

    markInvoiceSent(invoiceId: number, deliveryReference?: string, actorId?: number): Observable<Invoice> {
        return this.http.patch<Invoice>(`${this.apiUrl}/invoices/${invoiceId}/send`, { deliveryReference, actorId });
    }

    getAccountingDocumentAttachments(documentType: string, documentId: number): Observable<AccountingDocumentAttachment[]> {
        return this.http.get<AccountingDocumentAttachment[]>(`${this.apiUrl}/documents/${documentType}/${documentId}/attachments`);
    }

    saveAccountingDocumentAttachment(documentType: string, documentId: number, attachment: AccountingDocumentAttachment): Observable<AccountingDocumentAttachment> {
        return this.http.post<AccountingDocumentAttachment>(`${this.apiUrl}/documents/${documentType}/${documentId}/attachments`, attachment);
    }

    deleteAccountingDocumentAttachment(documentType: string, documentId: number, attachmentId: number, actorId?: number): Observable<void> {
        let params = new HttpParams();
        if (actorId !== undefined) params = params.set('actorId', actorId.toString());
        return this.http.delete<void>(`${this.apiUrl}/documents/${documentType}/${documentId}/attachments/${attachmentId}`, { params });
    }

    postInvoice(invoiceId: number, request?: DocumentWorkflowActionRequest): Observable<Invoice> {
        return this.http.post<Invoice>(`${this.apiUrl}/invoices/${invoiceId}/post`, request ?? {});
    }

    retryZatcaIntegration(invoiceId: number): Observable<Invoice> {
        return this.http.post<Invoice>(`${this.apiUrl}/invoices/zatca/retry/${invoiceId}`, {});
    }

    getEInvoiceComplianceDashboard(owningCompanyId: number): Observable<EInvoiceComplianceDashboard> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<EInvoiceComplianceDashboard>(`${this.apiUrl}/compliance/e-invoices/dashboard`, { params });
    }

    getComplianceEvents(documentType: string, documentId: number): Observable<AccountingComplianceEvent[]> {
        const params = new HttpParams()
            .set('documentType', documentType)
            .set('documentId', documentId.toString());
        return this.http.get<AccountingComplianceEvent[]>(`${this.apiUrl}/compliance/events`, { params });
    }

    private appendInvoiceFilters(params: HttpParams, filters?: {
        documentStatuses?: string[];
        workflowStatuses?: string[];
        postingStatuses?: string[];
        paymentStatuses?: string[];
        dueStatus?: string;
        sentStatuses?: string[];
        collectionStatuses?: string[];
        zatcaStatuses?: string[];
        currencyCode?: string;
        amountMin?: number;
        amountMax?: number;
        partyId?: number;
        partyType?: string;
    }): HttpParams {
        if (!filters) return params;
        const listParams: Array<[string, string[] | undefined]> = [
            ['documentStatuses', filters.documentStatuses],
            ['workflowStatuses', filters.workflowStatuses],
            ['postingStatuses', filters.postingStatuses],
            ['paymentStatuses', filters.paymentStatuses],
            ['sentStatuses', filters.sentStatuses],
            ['collectionStatuses', filters.collectionStatuses],
            ['zatcaStatuses', filters.zatcaStatuses],
        ];
        listParams.forEach(([key, value]) => {
            if (value?.length) params = params.set(key, value.join(','));
        });
        if (filters.dueStatus) params = params.set('dueStatus', filters.dueStatus);
        if (filters.currencyCode) params = params.set('currencyCode', filters.currencyCode);
        if (filters.amountMin !== undefined) params = params.set('amountMin', filters.amountMin.toString());
        if (filters.amountMax !== undefined) params = params.set('amountMax', filters.amountMax.toString());
        if (filters.partyId !== undefined) params = params.set('partyId', filters.partyId.toString());
        if (filters.partyType) params = params.set('partyType', filters.partyType);
        return params;
    }

    generateInvoiceFromAccountReceivables(accountReceivables: AccountReceivables[]): Observable<Invoice[]> {
        const url = `${this.apiUrl}/invoices/enterprise/generate`;
        const selectedReceivables = accountReceivables.map(({ receivableId }) => ({ receivableId }));
        return this.http.post<Invoice[]>(url, selectedReceivables)
            .pipe(catchError(error => {
                    console.error('Error generating invoice:', error);
                    return throwError(() => error);
                })
            );
    }

    generateReceiptFromInvoices(invoices: Invoice[]): Observable<Receipt> {
        const url = `${this.apiUrl}/receipts/enterprise/generate`;
        return this.http.post<Receipt>(url, invoices)
            .pipe(catchError(error => {
                    console.error('Error generating receipt:', error);
                    return throwError(() => error);
                })
            );
    }

    generateInvoicePDF(companyId: number, invoiceId: number, invoiceNumber: string, queryIdList: string[]) {
        const url = `https://us-central1-${environment.firebaseConfig.projectId}.cloudfunctions.net/generateEnterpriseInvoicePDF`;
        let payload = {
            companyId: companyId,
            invoiceId: invoiceId,
            invoiceNumber: invoiceNumber,
            queryIdList: queryIdList
        };
        return this.http.post<Invoice>(url, payload)
            .pipe(catchError(error => {
                    console.error('Error generating invoice:', error);
                    return throwError(() => new Error(error)); // Or handle the error as needed
                })
            );
    }

    createReceipt(receipt: Receipt): Observable<Receipt> {
        return this.http.post<Receipt>(this.apiUrl + '/receipts', receipt);
    }

    getPaymentLedgerDefaults(owningCompanyId: number): Observable<{cashAccountId: number | null; receivableAccountId: number | null; payableAccountId: number | null}> {
        return this.http.get<{cashAccountId: number | null; receivableAccountId: number | null; payableAccountId: number | null}>(
            `${this.apiUrl}/payment-ledger-defaults`, { params: { owningCompanyId } });
    }

    createReceiptWithAllocations(receipt: Receipt, allocationRequest: ReceiptAllocationRequest): Observable<Receipt> {
        return this.http.post<Receipt>(`${this.apiUrl}/receipts/with-allocations`, { receipt, allocationRequest });
    }

    // Receipts
    getReceipts(page: number, pageSize: number, companyId?: number, owningCompanyId?: number, companyGroupId?: number): Observable<any> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        if (companyId) {
            params = params.set('companyId', companyId.toString());
        }
        params = this.applyCompanyScope(params, 'owningCompanyId', owningCompanyId, companyGroupId);
        return this.http.get(this.apiUrl + '/receipts', { params });
    }

    getReceiptById(receiptId: number): Observable<Receipt> {
        return this.http.get<Receipt>(this.apiUrl + `/receipts/${receiptId}`);
    }

    downloadReceiptDocument(receiptId: number): Observable<Blob> {
        return this.downloadStoredDocument(`receipts/${receiptId}/document`);
    }

    getGeneratedReceipts(
        page: number,
        pageSize: number,
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        receiptNumber?: string,
        paymentTransactionNumber?: string,
        companyGroupId?: number,
        documentStatuses?: string[],
        searchText?: string
    ): Observable<APIListWrapper<Receipt>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        params = this.applyCompanyScope(params, 'companyId', companyId, companyGroupId);

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (receiptNumber) {
            params = params.set('receiptNumber', receiptNumber);
        }
        if (paymentTransactionNumber) {
            params = params.set('paymentTransactionNumber', paymentTransactionNumber);
        }
        if (documentStatuses?.length) params = params.set('documentStatuses', documentStatuses.join(','));
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<Receipt>>(`${this.apiUrl}/receipts/generated`, { params });
    }

    applyReceiptOnInvoices(receiptId: number, invoiceList: Invoice[]): Observable<Boolean> {
        const url = `${this.apiUrl}/receipts/apply/${receiptId}`;
        return this.http.patch<Boolean>(url, invoiceList)
            .pipe(catchError(error => {
                    console.error('Error applying receipt:', error);
                    return throwError(() => new Error(error)); // Or handle the error as needed
                })
            );
    }

    applyReceiptAllocations(receiptId: number, request: ReceiptAllocationRequest): Observable<Boolean> {
        const url = `${this.apiUrl}/receipts/${receiptId}/allocations`;
        return this.http.patch<Boolean>(url, request)
            .pipe(catchError(error => {
                    console.error('Error applying receipt allocations:', error);
                    return throwError(() => new Error(error));
                })
            );
    }

    runInvoiceWorkflow(invoiceId: number, action: AccountingDocumentAction, request?: DocumentWorkflowActionRequest): Observable<any> {
        return this.http.patch<any>(`${this.apiUrl}/invoices/${invoiceId}/workflow/${action}`, request ?? {});
    }

    runReceiptWorkflow(receiptId: number, action: AccountingDocumentAction, request?: DocumentWorkflowActionRequest): Observable<any> {
        return this.http.patch<any>(`${this.apiUrl}/receipts/${receiptId}/workflow/${action}`, request ?? {});
    }


    // Account Receivables
    getAccountReceivables(
        page: number,
        pageSize: number,
        owningCompanyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        referenceNumber?: string,
        invoiceNumber?: string,
        sourceTypes?: string[],
        readinessStatuses?: string[],
        includeHeld?: boolean,
        includeDisputed?: boolean,
        approvalRequired?: boolean,
        companyGroupId?: number,
        crmClientContactIds?: number[],
        sourceReference?: string,
        searchText?: string
    ): Observable<APIListWrapper<AccountReceivables>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        params = this.applyCompanyScope(params, 'owningCompanyId', owningCompanyId, companyGroupId);

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (crmClientContactIds && crmClientContactIds.length > 0) {
            params = params.set('crmClientContactIds', crmClientContactIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (referenceNumber) {
            params = params.set('referenceNumber', referenceNumber);
        }
        if (sourceReference) {
            params = params.set('sourceReference', sourceReference);
        }
        if (invoiceNumber) {
            params = params.set('invoiceNumber', invoiceNumber);
        }
        if (sourceTypes && sourceTypes.length > 0) {
            params = params.set('sourceTypes', sourceTypes.join(','));
        }
        if (readinessStatuses && readinessStatuses.length > 0) {
            params = params.set('readinessStatuses', readinessStatuses.join(','));
        }
        if (includeHeld !== undefined) {
            params = params.set('includeHeld', includeHeld.toString());
        }
        if (includeDisputed !== undefined) {
            params = params.set('includeDisputed', includeDisputed.toString());
        }
        if (approvalRequired !== undefined) {
            params = params.set('approvalRequired', approvalRequired.toString());
        }
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<AccountReceivables>>(`${this.apiUrl}/account-receivables`, { params });
    }

    createAccountReceivables(accountReceivables: AccountReceivables): Observable<AccountReceivables> {
        return this.http.post<AccountReceivables>(this.apiUrl + '/account-receivables', accountReceivables);
    }

    getAccountReceivableById(receivableId: number): Observable<AccountReceivables> {
        return this.http.get<AccountReceivables>(`${this.apiUrl}/account-receivables/${receivableId}`);
    }

    updateAccountReceivables(receivableId: number, accountReceivables: AccountReceivables): Observable<AccountReceivables> {
        return this.http.put<AccountReceivables>(`${this.apiUrl}/account-receivables/${receivableId}`, accountReceivables);
    }

	updateAccountReceivableReadiness(receivableId: number, update: {
		rowVersion?: number; status: string; holdReason?: string; disputeReason?: string; reviewReason?: string;
	}): Observable<AccountReceivables> {
		return this.http.patch<AccountReceivables>(`${this.apiUrl}/account-receivables/${receivableId}/readiness`, update);
	}

    getAccountPayables(
        page: number,
        pageSize: number,
        owningCompanyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        referenceNumber?: string,
        billNumber?: string,
        sourceTypes?: string[],
        readinessStatuses?: string[],
        includeHeld?: boolean,
        includeMatchExceptions?: boolean,
        approvalRequired?: boolean,
        companyGroupId?: number,
        crmClientContactIds?: number[],
        sourceReference?: string,
        searchText?: string
    ): Observable<APIListWrapper<AccountPayables>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        params = this.applyCompanyScope(params, 'owningCompanyId', owningCompanyId, companyGroupId);

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (crmClientContactIds && crmClientContactIds.length > 0) {
            params = params.set('crmClientContactIds', crmClientContactIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (referenceNumber) {
            params = params.set('referenceNumber', referenceNumber);
        }
        if (sourceReference) {
            params = params.set('sourceReference', sourceReference);
        }
        if (billNumber) {
            params = params.set('billNumber', billNumber);
        }
        if (sourceTypes && sourceTypes.length > 0) {
            params = params.set('sourceTypes', sourceTypes.join(','));
        }
        if (readinessStatuses && readinessStatuses.length > 0) {
            params = params.set('readinessStatuses', readinessStatuses.join(','));
        }
        if (includeHeld !== undefined) {
            params = params.set('includeHeld', includeHeld.toString());
        }
        if (includeMatchExceptions !== undefined) {
            params = params.set('includeMatchExceptions', includeMatchExceptions.toString());
        }
        if (approvalRequired !== undefined) {
            params = params.set('approvalRequired', approvalRequired.toString());
        }
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<AccountPayables>>(`${this.apiUrl}/account-payables`, { params });
    }

    createAccountPayables(accountPayables: AccountPayables): Observable<AccountPayables> {
        return this.http.post<AccountPayables>(this.apiUrl + '/account-payables', accountPayables);
    }

    getAccountPayableById(payableId: number): Observable<AccountPayables> {
        return this.http.get<AccountPayables>(`${this.apiUrl}/account-payables/${payableId}`);
    }

    updateAccountPayables(payableId: number, accountPayables: AccountPayables): Observable<AccountPayables> {
        return this.http.put<AccountPayables>(`${this.apiUrl}/account-payables/${payableId}`, accountPayables);
    }

	updateAccountPayableReadiness(payableId: number, update: {
		rowVersion?: number; status: string; holdReason?: string; matchExceptionReason?: string; reviewReason?: string;
	}): Observable<AccountPayables> {
		return this.http.patch<AccountPayables>(`${this.apiUrl}/account-payables/${payableId}/readiness`, update);
	}

    generateBillFromAccountPayables(accountPayables: AccountPayables[]): Observable<Bill[]> {
        const url = `${this.apiUrl}/bills/enterprise/generate`;
        const selectedPayables = accountPayables.map(({ payableId }) => ({ payableId }));
        return this.http.post<Bill[]>(url, selectedPayables)
            .pipe(catchError(error => {
                    console.error('Error generating bill:', error);
                    return throwError(() => error);
                })
            );
    }

    generateVoucherFromBills(bills: Bill[]): Observable<Voucher> {
        const url = `${this.apiUrl}/vouchers/enterprise/generate`;
        return this.http.post<Voucher>(url, bills)
            .pipe(catchError(error => {
                    console.error('Error generating voucher:', error);
                    return throwError(() => error);
                })
            );
    }

    getGeneratedBills(
        page: number,
        pageSize: number,
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        billNumber?: string,
        extraFilters?: {
            documentStatuses?: string[];
            workflowStatuses?: string[];
            postingStatuses?: string[];
            paymentStatuses?: string[];
            dueStatus?: string;
            matchStatuses?: string[];
            paymentHold?: boolean;
            vendorInvoiceNumber?: string;
            currencyCode?: string;
            amountMin?: number;
            amountMax?: number;
            partyId?: number;
            partyType?: string;
        },
        companyGroupId?: number,
        searchText?: string
    ): Observable<APIListWrapper<Bill>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        params = this.applyCompanyScope(params, 'companyId', companyId, companyGroupId);

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (billNumber) {
            params = params.set('billNumber', billNumber);
        }
        params = this.appendBillFilters(params, extraFilters);
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<Bill>>(`${this.apiUrl}/bills/generated`, { params });
    }

    getBillById(billId: number): Observable<Bill> {
        return this.http.get<Bill>(`${this.apiUrl}/bills/${billId}`);
    }

    downloadBillDocument(billId: number): Observable<Blob> {
        return this.downloadStoredDocument(`bills/${billId}/document`);
    }

    billDocumentStatus(billId: number): Observable<FinancePdfStatus> {
        return this.http.get<FinancePdfStatus>(`${this.apiUrl}/bills/${billId}/document/status`);
    }

    reprintBillDocument(billId: number): Observable<Blob> {
        return this.downloadStoredDocument(`bills/${billId}/document/reprint`);
    }

    postBill(billId: number, request?: DocumentWorkflowActionRequest): Observable<Bill> {
        return this.http.post<Bill>(`${this.apiUrl}/bills/${billId}/post`, request ?? {});
    }

    checkDuplicateVendorBill(owningCompanyId: number, vendorId: number, vendorInvoiceNumber: string, vendorInvoiceDate?: string, totalAmount?: number, excludeBillId?: number, vendorPartyType?: string): Observable<{ duplicate: boolean }> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('vendorId', vendorId.toString())
            .set('vendorInvoiceNumber', vendorInvoiceNumber);
        if (vendorInvoiceDate) params = params.set('vendorInvoiceDate', vendorInvoiceDate);
        if (totalAmount !== undefined) params = params.set('totalAmount', totalAmount.toString());
        if (excludeBillId !== undefined) params = params.set('excludeBillId', excludeBillId.toString());
        if (vendorPartyType) params = params.set('vendorPartyType', vendorPartyType);
        return this.http.get<{ duplicate: boolean }>(`${this.apiUrl}/bills/duplicate-check`, { params });
    }

    createNonPoBillDraft(bill: Bill): Observable<Bill> {
        return this.http.post<Bill>(`${this.apiUrl}/bills/non-po-draft`, bill);
    }

    updateNonPoBillDraft(billId: number, draft: NonPoBillDraftUpdateRequest): Observable<Bill> {
        return this.http.patch<Bill>(`${this.apiUrl}/bills/non-po-draft/${billId}`, draft);
    }

    updateBillDraft(billId: number, draft: NonPoBillDraftUpdateRequest): Observable<Bill> {
        return this.http.patch<Bill>(`${this.apiUrl}/bills/${billId}`, draft);
    }

    createBill(bill: Bill): Observable<Bill> {
        return this.http.post<Bill>(this.apiUrl + '/bills', bill);
    }

    updateBill(billId: number, bill: Bill): Observable<Bill> {
        return this.http.put<Bill>(`${this.apiUrl}/bills/${billId}`, bill);
    }

    updateBillCommercialAnnotations(billId: number, request: CommercialAnnotationsUpdateRequest): Observable<Bill> {
        return this.http.patch<Bill>(`${this.apiUrl}/bills/${billId}/commercial-annotations`, request);
    }

    private appendBillFilters(params: HttpParams, filters?: {
        documentStatuses?: string[];
        workflowStatuses?: string[];
        postingStatuses?: string[];
        paymentStatuses?: string[];
        dueStatus?: string;
        matchStatuses?: string[];
        paymentHold?: boolean;
        vendorInvoiceNumber?: string;
        currencyCode?: string;
        amountMin?: number;
        amountMax?: number;
        partyId?: number;
        partyType?: string;
    }): HttpParams {
        if (!filters) return params;
        const listParams: Array<[string, string[] | undefined]> = [
            ['documentStatuses', filters.documentStatuses],
            ['workflowStatuses', filters.workflowStatuses],
            ['postingStatuses', filters.postingStatuses],
            ['paymentStatuses', filters.paymentStatuses],
            ['matchStatuses', filters.matchStatuses],
        ];
        listParams.forEach(([key, value]) => {
            if (value?.length) params = params.set(key, value.join(','));
        });
        if (filters.dueStatus) params = params.set('dueStatus', filters.dueStatus);
        if (filters.paymentHold !== undefined) params = params.set('paymentHold', filters.paymentHold.toString());
        if (filters.vendorInvoiceNumber) params = params.set('vendorInvoiceNumber', filters.vendorInvoiceNumber);
        if (filters.currencyCode) params = params.set('currencyCode', filters.currencyCode);
        if (filters.amountMin !== undefined) params = params.set('amountMin', filters.amountMin.toString());
        if (filters.amountMax !== undefined) params = params.set('amountMax', filters.amountMax.toString());
        if (filters.partyId !== undefined) params = params.set('partyId', filters.partyId.toString());
        if (filters.partyType) params = params.set('partyType', filters.partyType);
        return params;
    }

    getGeneratedVouchers(
        page: number,
        pageSize: number,
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        voucherNumber?: string,
        paymentTransactionNumber?: string,
        companyGroupId?: number,
        documentStatuses?: string[],
        searchText?: string
    ): Observable<APIListWrapper<Voucher>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        params = this.applyCompanyScope(params, 'companyId', companyId, companyGroupId);

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (voucherNumber) {
            params = params.set('voucherNumber', voucherNumber);
        }
        if (paymentTransactionNumber) {
            params = params.set('paymentTransactionNumber', paymentTransactionNumber);
        }
        if (documentStatuses?.length) params = params.set('documentStatuses', documentStatuses.join(','));
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<APIListWrapper<Voucher>>(`${this.apiUrl}/vouchers/generated`, { params });
    }

    getVoucherById(voucherId: number): Observable<Voucher> {
        return this.http.get<Voucher>(`${this.apiUrl}/vouchers/${voucherId}`);
    }

    downloadVoucherDocument(voucherId: number): Observable<Blob> {
        return this.downloadStoredDocument(`vouchers/${voucherId}/document`);
    }

    private downloadStoredDocument(path: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/${path}`, { responseType: 'blob', observe: 'response' }).pipe(
            map(response => rememberFinancePdfVariant(response.body!, response.headers.get('X-Finance-Pdf-Variant')))
        );
    }

    financePdfStatus(kind: FinanceDocumentKind, id: number): Observable<FinancePdfStatus> {
        const route = kind === 'vendor-bill' ? 'bills' : `${kind}s`;
        return this.http.get<FinancePdfStatus>(`${this.apiUrl}/${route}/${id}/document/status`);
    }

    downloadPdfDocument(
        pdf: Blob,
        kind: FinanceDocumentKind,
        documentReference?: string | null,
        documentId?: number | null,
        postingStatus?: string | null
    ): void {
        downloadFinancePdf(pdf, kind, documentReference, documentId, postingStatus);
    }

    async viewPdfDocument<TKey>(
        kind: FinanceDocumentKind,
        documentId: number | null | undefined,
        documentReference: string | null | undefined,
        postingStatus: string | null | undefined,
        pending: PendingActionTracker<TKey>,
        key: TKey,
        reprint = false,
        onConflict?: () => void
    ): Promise<void> {
        if (!documentId || !pending.begin(key)) return;
        try {
            if (reprint && kind !== 'invoice' && kind !== 'vendor-bill') throw new Error('Reprint is unavailable for this document type');
            const request = reprint
                ? kind === 'invoice' ? this.reprintInvoiceDocument(documentId) : this.reprintBillDocument(documentId)
                : kind === 'invoice' ? this.downloadInvoiceDocument(documentId)
                : kind === 'vendor-bill' ? this.downloadBillDocument(documentId)
                : kind === 'receipt' ? this.downloadReceiptDocument(documentId)
                : kind === 'voucher' ? this.downloadVoucherDocument(documentId)
                : kind === 'credit-note' ? this.downloadCreditNoteDocument(documentId)
                : this.downloadDebitNoteDocument(documentId);
            const pdf = await firstValueFrom(request);
            const { FinancePdfViewerComponent } = await import('./finance-pdf-viewer.component');
            const locale = this.injector.get(LocaleService);
            const data: { pdf?: Blob; reference: string; variant: string } = {
                pdf,
                reference: documentReference?.trim() || `${kind} ${documentId}`,
                variant: reprint ? 'REPRINT' : financePdfVariant(pdf) || (postingStatus === 'POSTED' ? 'LEGACY' : 'DRAFT')
            };
            const dialog = this.injector.get(DialogService).open(FinancePdfViewerComponent, {
                header: locale.translate('financePdfViewer.title'),
                modal: true,
                closable: true,
                closeOnEscape: true,
                dismissableMask: false,
                draggable: false,
                resizable: false,
                width: 'min(1100px, 96vw)',
                breakpoints: { '640px': '100vw' },
                contentStyle: { padding: '0', overflow: 'hidden' },
                data
            });
            dialog?.onClose.subscribe(() => { data.pdf = undefined; });
        } catch (error: any) {
            if (error?.status === 409) onConflict?.();
            const locale = this.injector.get(LocaleService);
            const messageKey = error?.status === 404 ? 'unavailable'
                : error?.status === 403 ? 'denied'
                : error?.status === 409 ? 'reprintChanged' : 'failed';
            this.injector.get(MessageService).add({
                severity: 'error',
                summary: locale.translate('financePdfViewer.errorTitle'),
                detail: locale.translate(`financePdfViewer.${messageKey}`)
            });
        } finally {
            pending.end(key);
        }
    }

    createVoucher(voucher: Voucher): Observable<Voucher> {
        return this.http.post<Voucher>(this.apiUrl + '/vouchers', voucher);
    }

    createVoucherWithAllocations(voucher: Voucher, allocationRequest: VoucherAllocationRequest): Observable<Voucher> {
        return this.http.post<Voucher>(`${this.apiUrl}/vouchers/with-allocations`, { voucher, allocationRequest });
    }

    updateVoucher(voucherId: number, voucher: Voucher): Observable<Voucher> {
        return this.http.put<Voucher>(`${this.apiUrl}/vouchers/${voucherId}`, voucher);
    }

    applyVoucherOnBills(voucherId: number, billList: Bill[]): Observable<Boolean> {
        const url = `${this.apiUrl}/vouchers/apply/${voucherId}`;
        return this.http.patch<Boolean>(url, billList)
            .pipe(catchError(error => {
                    console.error('Error applying voucher:', error);
                    return throwError(() => error);
                })
            );
    }

    applyVoucherAllocations(voucherId: number, request: VoucherAllocationRequest): Observable<Boolean> {
        const url = `${this.apiUrl}/vouchers/${voucherId}/allocations`;
        return this.http.patch<Boolean>(url, request)
            .pipe(catchError(error => {
                    console.error('Error applying voucher allocations:', error);
                    return throwError(() => error);
                })
            );
    }

    runBillWorkflow(billId: number, action: AccountingDocumentAction, request?: DocumentWorkflowActionRequest): Observable<any> {
        return this.http.patch<any>(`${this.apiUrl}/bills/${billId}/workflow/${action}`, request ?? {});
    }

    runVoucherWorkflow(voucherId: number, action: AccountingDocumentAction, request?: DocumentWorkflowActionRequest): Observable<any> {
        return this.http.patch<any>(`${this.apiUrl}/vouchers/${voucherId}/workflow/${action}`, request ?? {});
    }

    getApMatchingPolicies(owningCompanyId: number): Observable<ApMatchingPolicy[]> {
        return this.http.get<ApMatchingPolicy[]>(`${this.apiUrl}/ap-matching/policies`, {
            params: new HttpParams().set('owningCompanyId', owningCompanyId.toString())
        });
    }

    saveApMatchingPolicy(policy: ApMatchingPolicy): Observable<ApMatchingPolicy> {
        if (policy.policyId) {
            return this.http.put<ApMatchingPolicy>(`${this.apiUrl}/ap-matching/policies/${policy.policyId}`, policy);
        }
        return this.http.post<ApMatchingPolicy>(`${this.apiUrl}/ap-matching/policies`, policy);
    }

    getApMatchingTolerances(owningCompanyId: number): Observable<ApMatchingTolerance[]> {
        return this.http.get<ApMatchingTolerance[]>(`${this.apiUrl}/ap-matching/tolerances`, {
            params: new HttpParams().set('owningCompanyId', owningCompanyId.toString())
        });
    }

    saveApMatchingTolerance(tolerance: ApMatchingTolerance): Observable<ApMatchingTolerance> {
        return this.http.post<ApMatchingTolerance>(`${this.apiUrl}/ap-matching/tolerances`, tolerance);
    }

    evaluateBillMatch(billId: number, request: BillMatchEvaluationRequest): Observable<BillMatchEvaluation> {
        return this.http.post<BillMatchEvaluation>(`${this.apiUrl}/ap-matching/bills/${billId}/evaluate`, request);
    }

    getBillMatchEvaluation(billId: number): Observable<BillMatchEvaluation> {
        return this.http.get<BillMatchEvaluation>(`${this.apiUrl}/ap-matching/bills/${billId}/evaluation`);
    }

    overrideBillMatch(billId: number, actorId: number | undefined, reason: string): Observable<BillMatchEvaluation> {
        return this.http.post<BillMatchEvaluation>(`${this.apiUrl}/ap-matching/bills/${billId}/override`, { actorId, reason });
    }

    getApMatchingDashboard(owningCompanyId: number): Observable<ApMatchingDashboard> {
        return this.http.get<ApMatchingDashboard>(`${this.apiUrl}/ap-matching/dashboard`, {
            params: new HttpParams().set('owningCompanyId', owningCompanyId.toString())
        });
    }

    generatePaymentProposal(request: PaymentProposalGenerateRequest): Observable<PaymentProposal> {
        return this.http.post<PaymentProposal>(`${this.apiUrl}/payment-proposals/generate`, request);
    }

    searchPaymentProposals(owningCompanyId: number, statuses?: string[]): Observable<PaymentProposal[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        if (statuses?.length) params = params.set('statuses', statuses.join(','));
        return this.http.get<PaymentProposal[]>(`${this.apiUrl}/payment-proposals/search`, { params });
    }

    getPaymentProposal(proposalId: number): Observable<PaymentProposal> {
        return this.http.get<PaymentProposal>(`${this.apiUrl}/payment-proposals/${proposalId}`);
    }

    approvePaymentProposal(proposalId: number, actorId?: number, reason?: string): Observable<PaymentProposal> {
        return this.http.post<PaymentProposal>(`${this.apiUrl}/payment-proposals/${proposalId}/approve`, { actorId, reason });
    }

    releasePaymentProposal(proposalId: number, actorId?: number, reason?: string): Observable<PaymentProposal> {
        return this.http.post<PaymentProposal>(`${this.apiUrl}/payment-proposals/${proposalId}/release`, { actorId, reason });
    }

    generatePaymentProposalVouchers(proposalId: number): Observable<PaymentProposal> {
        return this.http.post<PaymentProposal>(`${this.apiUrl}/payment-proposals/${proposalId}/generate-vouchers`, {});
    }

    generatePaymentProposalRemittance(proposalId: number, actorId?: number): Observable<RemittanceAdvice[]> {
        return this.http.post<RemittanceAdvice[]>(`${this.apiUrl}/payment-proposals/${proposalId}/remittance`, { actorId });
    }

    verifyVendorBankAccount(bankAccountId: number, actorId: number | undefined, reason: string, verificationStatus = 'VERIFIED'): Observable<BankAccount> {
        return this.http.post<BankAccount>(`${this.apiUrl}/vendor-bank-accounts/${bankAccountId}/verify`, { actorId, reason, verificationStatus });
    }

    getVendorBankAccountAudit(bankAccountId: number): Observable<VendorBankAccountAudit[]> {
        return this.http.get<VendorBankAccountAudit[]>(`${this.apiUrl}/vendor-bank-accounts/${bankAccountId}/audit`);
    }

    getClientReceivablesSummary(
        owningCompanyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        referenceNumber?: string,
        invoiceNumber?: string,
        companyGroupId?: number
    ): Observable<any> { // The component expects a single summary object, not an array.
        let params = new HttpParams();

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (referenceNumber) {
            params = params.set('referenceNumber', referenceNumber);
        }
        if (invoiceNumber) {
            params = params.set('invoiceNumber', invoiceNumber);
        }

        const url = companyGroupId ? `${this.apiUrl}/summary/receivables` : `${this.apiUrl}/summary/receivables/${owningCompanyId}`;
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId.toString());
        }
        return this.http.get<any>(url, { params });
    }

    getGeneratedInvoicesSummary(
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        invoiceNumber?: string,
        extraFilters?: {
            documentStatuses?: string[];
            workflowStatuses?: string[];
            postingStatuses?: string[];
            paymentStatuses?: string[];
            dueStatus?: string;
            sentStatuses?: string[];
            collectionStatuses?: string[];
            zatcaStatuses?: string[];
            currencyCode?: string;
            amountMin?: number;
            amountMax?: number;
        },
        companyGroupId?: number
    ): Observable<GeneratedInvoicesSummaryData> {
        let params = new HttpParams();

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (invoiceNumber) {
            params = params.set('invoiceNumber', invoiceNumber);
        }
        params = this.appendInvoiceFilters(params, extraFilters);
        const url = companyGroupId ? `${this.apiUrl}/summary/invoices` : `${this.apiUrl}/summary/invoices/${companyId}`;
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId.toString());
        }
        return this.http.get<GeneratedInvoicesSummaryData>(url, { params });
    }

    getGeneratedReceiptsSummary(
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        receiptNumber?: string,
        paymentTransactionNumber?: string,
        companyGroupId?: number
    ): Observable<GeneratedReceiptsSummaryData> {
        let params = new HttpParams();

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (receiptNumber) {
            params = params.set('receiptNumber', receiptNumber);
        }
        if (paymentTransactionNumber) {
            params = params.set('paymentTransactionNumber', paymentTransactionNumber);
        }
        const url = companyGroupId ? `${this.apiUrl}/summary/receipts/generated` : `${this.apiUrl}/summary/receipts/generated/${companyId}`;
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId.toString());
        }
        return this.http.get<GeneratedReceiptsSummaryData>(url, { params });
    }

    getClientPayablesSummary(
        owningCompanyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        referenceNumber?: string,
        billNumber?: string,
        companyGroupId?: number
    ): Observable<any> { // The component expects a single summary object, not an array.
        let params = new HttpParams();

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (referenceNumber) {
            params = params.set('referenceNumber', referenceNumber);
        }
        if (billNumber) {
            params = params.set('billNumber', billNumber);
        }

        const url = companyGroupId ? `${this.apiUrl}/summary/payables` : `${this.apiUrl}/summary/payables/${owningCompanyId}`;
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId.toString());
        }
        return this.http.get<any>(url, { params });
    }

    getGeneratedBillsSummary(
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        billNumber?: string,
        extraFilters?: {
            documentStatuses?: string[];
            workflowStatuses?: string[];
            postingStatuses?: string[];
            paymentStatuses?: string[];
            dueStatus?: string;
            matchStatuses?: string[];
            paymentHold?: boolean;
            vendorInvoiceNumber?: string;
            currencyCode?: string;
            amountMin?: number;
            amountMax?: number;
        },
        companyGroupId?: number
    ): Observable<GeneratedInvoicesSummaryData> {
        let params = new HttpParams();

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (billNumber) {
            params = params.set('billNumber', billNumber);
        }
        params = this.appendBillFilters(params, extraFilters);
        const url = companyGroupId ? `${this.apiUrl}/summary/bills` : `${this.apiUrl}/summary/bills/${companyId}`;
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId.toString());
        }
        return this.http.get<GeneratedInvoicesSummaryData>(url, { params });
    }

    getGeneratedVouchersSummary(
        companyId?: number,
        clientIds?: number[],
        statuses?: string[],
        startDate?: string,
        endDate?: string,
        voucherNumber?: string,
        paymentTransactionNumber?: string,
        companyGroupId?: number
    ): Observable<GeneratedVouchersSummaryData> {
        let params = new HttpParams();

        if (clientIds && clientIds.length > 0) {
            params = params.set('clientIds', clientIds.join(','));
            params = params.set('crmClientIds', clientIds.join(','));
        }
        if (statuses && statuses.length > 0) {
            params = params.set('statuses', statuses.join(','));
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        if (voucherNumber) {
            params = params.set('voucherNumber', voucherNumber);
        }
        if (paymentTransactionNumber) {
            params = params.set('paymentTransactionNumber', paymentTransactionNumber);
        }
        const url = companyGroupId ? `${this.apiUrl}/summary/vouchers` : `${this.apiUrl}/summary/vouchers/${companyId}`;
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId.toString());
        }
        return this.http.get<GeneratedVouchersSummaryData>(url, { params });
    }

    // Credit Note Methods
    getAllCreditNotes(
        owningCompanyId: number | undefined,
        page: number,
        pageSize: number,
        companyGroupId?: number,
        zatcaStatuses?: string[],
        crmClientIds?: number[],
        documentStatuses?: string[],
        searchText?: string
    ): Observable<any> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        if (zatcaStatuses?.length) params = params.set('zatcaStatuses', zatcaStatuses.join(','));
        if (crmClientIds?.length) params = params.set('crmClientIds', crmClientIds.join(','));
        if (documentStatuses?.length) params = params.set('documentStatuses', documentStatuses.join(','));
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<any>(`${this.apiUrl}/credit-notes`, {
            params: this.applyCompanyScope(params, 'owningCompanyId', owningCompanyId, companyGroupId)
        });
    }

    getCreditNoteById(id: number): Observable<CreditNote> {
        return this.http.get<CreditNote>(`${this.apiUrl}/credit-notes/${id}`);
    }

    downloadCreditNoteDocument(id: number): Observable<Blob> {
        return this.downloadStoredDocument(`credit-notes/${id}/document`);
    }

    createCreditNote(creditNote: CreditNote): Observable<CreditNote> {
        return this.http.post<CreditNote>(
            `${this.apiUrl}/credit-notes`,
            creditNote
        );
    }

    deleteCreditNote(id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/credit-notes/${id}`);
    }

    runCreditNoteWorkflow(id: number, action: AccountingDocumentAction, request?: DocumentWorkflowActionRequest): Observable<any> {
        return this.http.patch<any>(`${this.apiUrl}/credit-notes/${id}/workflow/${action}`, request ?? {});
    }

    getCreditNoteAuditEvents(id: number): Observable<DocumentAuditEvent[]> {
        return this.http.get<DocumentAuditEvent[]>(`${this.apiUrl}/credit-notes/${id}/audit-events`);
    }

    getCreditNoteGlPreview(id: number): Observable<GlPreview> {
        return this.http.get<GlPreview>(`${this.apiUrl}/credit-notes/${id}/gl-preview`);
    }

    retryCreditNoteZatca(id: number, actorId?: number): Observable<any> {
        let params = new HttpParams();
        if (actorId !== undefined) params = params.set('actorId', actorId.toString());
        return this.http.post<any>(`${this.apiUrl}/credit-notes/${id}/zatca/retry`, {}, { params });
    }

    // ZATCA Onboarding Methods
    getZatcaOnboardingStatus(owningCompanyId: number): Observable<ZatcaOnboardingStatus> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ZatcaOnboardingStatus>(`${this.apiUrl}/compliance/zatca/status`, { params });
    }

    onboardZatca(registration: ZatcaBusinessRegistration): Observable<ZatcaOnboardingResponse> {
        return this.http.post<ZatcaOnboardingResponse>(`${this.apiUrl}/compliance/zatca/onboard`, registration);
    }

    // --- Bayan Naql Onboarding ---

    getBayanOnboardingStatus(owningCompanyId: number): Observable<BayanOnboardingStatus> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<BayanOnboardingStatus>(`${this.apiUrl}/compliance/bayan/status`, { params });
    }

    onboardBayan(owningCompanyId: number, clientId: string): Observable<BayanOnboardingResponse> {
        return this.http.post<BayanOnboardingResponse>(`${this.apiUrl}/compliance/bayan/onboard`, { owningCompanyId, clientId });
    }

    deboardBayan(owningCompanyId: number): Observable<BayanOnboardingResponse> {
        return this.http.delete<BayanOnboardingResponse>(`${this.apiUrl}/compliance/bayan/onboard?owningCompanyId=${owningCompanyId}`);
    }

    applyCreditNote(id: number, reason?: string, actorId?: number): Observable<CreditNote> {
        return this.http.patch<CreditNote>(`${this.apiUrl}/credit-notes/${id}/apply`, { reason, actorId });
    }

    // Debit Note Methods
    getAllDebitNotes(
        owningCompanyId: number | undefined,
        page: number,
        pageSize: number,
        companyGroupId?: number,
        zatcaStatuses?: string[],
        crmClientIds?: number[],
        documentStatuses?: string[],
        searchText?: string
    ): Observable<any> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        if (zatcaStatuses?.length) params = params.set('zatcaStatuses', zatcaStatuses.join(','));
        if (crmClientIds?.length) params = params.set('crmClientIds', crmClientIds.join(','));
        if (documentStatuses?.length) params = params.set('documentStatuses', documentStatuses.join(','));
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<any>(`${this.apiUrl}/debit-notes`, {
            params: this.applyCompanyScope(params, 'owningCompanyId', owningCompanyId, companyGroupId)
        });
    }

    getDebitNoteById(id: number): Observable<DebitNote> {
        return this.http.get<DebitNote>(`${this.apiUrl}/debit-notes/${id}`);
    }

    downloadDebitNoteDocument(id: number): Observable<Blob> {
        return this.downloadStoredDocument(`debit-notes/${id}/document`);
    }

    createDebitNote(debitNote: DebitNote): Observable<DebitNote> {
        return this.http.post<DebitNote>(
            `${this.apiUrl}/debit-notes`,
            debitNote
        );
    }

    deleteDebitNote(id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/debit-notes/${id}`);
    }

    runDebitNoteWorkflow(id: number, action: AccountingDocumentAction, request?: DocumentWorkflowActionRequest): Observable<any> {
        return this.http.patch<any>(`${this.apiUrl}/debit-notes/${id}/workflow/${action}`, request ?? {});
    }

    getDebitNoteAuditEvents(id: number): Observable<DocumentAuditEvent[]> {
        return this.http.get<DocumentAuditEvent[]>(`${this.apiUrl}/debit-notes/${id}/audit-events`);
    }

    getDebitNoteGlPreview(id: number): Observable<GlPreview> {
        return this.http.get<GlPreview>(`${this.apiUrl}/debit-notes/${id}/gl-preview`);
    }

    retryDebitNoteZatca(id: number, actorId?: number): Observable<any> {
        let params = new HttpParams();
        if (actorId !== undefined) params = params.set('actorId', actorId.toString());
        return this.http.post<any>(`${this.apiUrl}/debit-notes/${id}/zatca/retry`, {}, { params });
    }

    applyDebitNote(id: number, reason?: string, actorId?: number): Observable<DebitNote> {
        return this.http.patch<DebitNote>(`${this.apiUrl}/debit-notes/${id}/apply`, { reason, actorId });
    }

    getAuditEvents(documentType: AccountingDocumentType, documentId: number): Observable<DocumentAuditEvent[]> {
        const params = new HttpParams()
            .set('documentType', documentType)
            .set('documentId', documentId.toString());
        return this.http.get<DocumentAuditEvent[]>(`${this.apiUrl}/audit-events`, { params });
    }

    getGlPreview(documentType: Exclude<AccountingDocumentType, 'DEBIT_NOTE' | 'CREDIT_NOTE' | 'JOURNAL_ENTRY'>, documentId: number): Observable<GlPreview> {
        const endpoints: Record<string, string> = {
            INVOICE: 'invoices',
            BILL: 'bills',
            RECEIPT: 'receipts',
            VOUCHER: 'vouchers'
        };
        return this.http.get<GlPreview>(`${this.apiUrl}/${endpoints[documentType]}/${documentId}/gl-preview`);
    }

}
